import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { postUser, getNextSocioNumberPreview } from "../../api/user";
import { Button, TextField } from "@material-ui/core";
import { alertError } from "../../utils/logger";
import { Autocomplete } from "@material-ui/lab";

export const UserFormModal = ({
  open,
  setOpen,
  onSave,
  userTypes,
  defaultUserType,
  submitRole = "user",
}) => {
  const [formData, setFormData] = useState({
    fullName: "",
    apellido1: "",
    apellido2: "",
    dni: "",
    email: "",
    telefono: "",
  });
  const [selectedUserType, setSelectedUserType] = useState(defaultUserType || {});

  const [errors, setErrors] = useState({});
  const [nSocioPreview, setNSocioPreview] = useState(null);
  const [nSocioPreviewLoading, setNSocioPreviewLoading] = useState(false);

  // Limpiar formulario cuando se abre el modal
  useEffect(() => {
    if (open) {
      setFormData({
        fullName: "",
        apellido1: "",
        apellido2: "",
        dni: "",
        email: "",
        telefono: "",
      });
      setErrors({});
      // Establecer el tipo de usuario por defecto si se proporciona
      if (defaultUserType) {
        setSelectedUserType(defaultUserType);
      }
      // Prevenir scroll del body cuando el modal está abierto
      document.body.style.overflow = "hidden";
    } else {
      // Restaurar scroll del body cuando el modal se cierra
      document.body.style.overflow = "unset";
    }

    // Cleanup function para restaurar scroll si el componente se desmonta
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open, defaultUserType]);

  useEffect(() => {
    if (!open || selectedUserType?.asociado !== true) {
      setNSocioPreview(null);
      setNSocioPreviewLoading(false);
      return;
    }
    let cancelled = false;
    setNSocioPreviewLoading(true);
    getNextSocioNumberPreview()
      .then((res) => {
        if (
          !cancelled &&
          res.status === 200 &&
          res.data?.nextNAso != null
        ) {
          setNSocioPreview(res.data.nextNAso);
        }
      })
      .catch(() => {
        if (!cancelled) setNSocioPreview(null);
      })
      .finally(() => {
        if (!cancelled) setNSocioPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, selectedUserType?.asociado, selectedUserType?._id]);

  const handleInputChange = (name) => (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Limpiar error del campo cuando el usuario empieza a escribir
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "El nombre completo es requerido";
    }

    if (!formData.dni.trim()) {
      newErrors.dni = "El DNI es requerido";
    } else if (!/^\d{8}[A-Z]?$/.test(formData.dni.trim())) {
      newErrors.dni = "El DNI debe tener 8 dígitos y opcionalmente una letra";
    }

    if (!formData.email.trim()) {
      newErrors.email = "El email es requerido";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "El email no tiene un formato válido";
    }

    if (!formData.telefono.trim()) {
      newErrors.telefono = "El teléfono es requerido";
    } else if (!/^\d{9}$/.test(formData.telefono.trim())) {
      newErrors.telefono = "El teléfono debe tener 9 dígitos";
    }

    if (!formData.apellido1?.trim()) {
      newErrors.apellido1 = "El apellido 1 es requerido";
    }
    if (!formData.apellido2?.trim()) {
      newErrors.apellido2 = "El apellido 2 es requerido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const payload = {
        ...formData,
        role: submitRole,
        asociado: selectedUserType?.asociado === true,
      };
      if (selectedUserType?._id) {
        payload.type = selectedUserType._id;
      }
      if (submitRole === "docente") {
        payload.esDocente = true;
      }
      postUser(payload)
        .then((resUser) => {
          onSave(resUser.data);
          setOpen(false);
        })
        .catch((error) =>
          alertError({
            error: error,
            customMessage: "Could not save user.",
          })
        );
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Manejar click en el backdrop - deshabilitado para evitar pérdida de datos
  const handleBackdropClick = (e) => {
    // No hacer nada - el modal solo se cierra con los botones
    e.stopPropagation();
  };

  // Manejar tecla Escape - deshabilitado para evitar pérdida de datos
  // El modal solo se cierra con los botones
  useEffect(() => {
    // No hacer nada - el modal solo se cierra con los botones
  }, [open]);

  if (!open) return null;

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      onClick={handleBackdropClick}
      style={{
        zIndex: 1400,
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
      }}
    >
      <div
        className="bg-white rounded-lg p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "auto",
          minWidth: "400px",
          maxWidth: "500px",
          maxHeight: "90vh",
          overflowY: "auto",
          margin: "20px",
          position: "relative",
          zIndex: 1400,
        }}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            Formulario de Usuario
          </h2>
        </div>

        <div className="space-y-4" style={{ position: "relative", zIndex: 1401 }}>
          <div>
            <Autocomplete
              className="w-100 mr-5"
              id="autocomplete-familia"
              options={userTypes}
              getOptionLabel={(option) => option?.titulo}
              value={selectedUserType || ""}
              onChange={(event, selected) => {
                setSelectedUserType(selected);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label="Seleccionar Tipo de usuario"
                />
              )}
              PopperProps={{
                style: { zIndex: 1500 },
                disablePortal: false,
              }}
            />
          </div>
          {selectedUserType?.asociado === true && (
            <TextField
              label="Nº Socio (previsión)"
              value={
                nSocioPreviewLoading
                  ? "…"
                  : nSocioPreview != null
                    ? String(nSocioPreview)
                    : ""
              }
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              fullWidth
              helperText="Según el contador en este momento; el definitivo lo asigna el servidor al guardar."
            />
          )}
          <div>
            <TextField
              id={`fullName`}
              label="Nombre"
              value={formData.fullName}
              onChange={handleInputChange("fullName")}
              InputLabelProps={{
                shrink: true,
              }}
              margin="normal"
              placeholder="Ingresa nombre"
              variant="outlined"
              required
              style={{ position: "relative", zIndex: 1401 }}
            />
            {errors.fullName && (
              <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>
            )}
          </div>
          <div>
            <TextField
              id={`apellido1`}
              label="Apellido 1"
              value={formData.apellido1}
              onChange={handleInputChange("apellido1")}
              InputLabelProps={{
                shrink: true,
              }}
              margin="normal"
              placeholder="Ingresa nombre completo"
              variant="outlined"
              required
              style={{ position: "relative", zIndex: 1401 }}
            />
            {errors.apellido1 && (
              <p className="text-red-500 text-sm mt-1">{errors.apellido1}</p>
            )}
          </div>
          <div>
            <TextField
              id={`apellido2`}
              label="Apellido 2"
              value={formData.apellido2}
              onChange={handleInputChange("apellido2")}
              InputLabelProps={{
                shrink: true,
              }}
              margin="normal"
              placeholder="Ingresa nombre completo"
              variant="outlined"
              required
              style={{ position: "relative", zIndex: 1401 }}
            />
            {errors.apellido2 && (
              <p className="text-red-500 text-sm mt-1">{errors.apellido2}</p>
            )}
          </div>

          <div>
            <TextField
              id={`dni`}
              label="DNI"
              value={formData.dni}
              onChange={handleInputChange("dni")}
              InputLabelProps={{
                shrink: true,
              }}
              placeholder="12345678A"
              margin="normal"
              variant="outlined"
              style={{ position: "relative", zIndex: 1401 }}
            />
            {errors.dni && (
              <p className="text-red-500 text-sm mt-1">{errors.dni}</p>
            )}
          </div>

          <div>
            <TextField
              id={`email`}
              label="Email"
              value={formData.email}
              onChange={handleInputChange("email")}
              InputLabelProps={{
                shrink: true,
              }}
              placeholder="ejemplo@correo.com"
              margin="normal"
              variant="outlined"
              required
              style={{ position: "relative", zIndex: 1401 }}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <TextField
              id={`telefono`}
              label="Teléfono"
              value={formData.telefono}
              onChange={handleInputChange("telefono")}
              InputLabelProps={{
                shrink: true,
              }}
              placeholder="123456789"
              margin="normal"
              variant="outlined"
              style={{ position: "relative", zIndex: 1401 }}
            />
            {errors.telefono && (
              <p className="text-red-500 text-sm mt-1">{errors.telefono}</p>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              onClick={handleClose}
              variant="outlined"
              style={{ marginRight: "20px" }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              variant="outlined"
              color="primary"
              style={{ marginRight: "20px" }}
            >
              Guardar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  // Usar createPortal para renderizar el modal en el body
  return createPortal(modalContent, document.body);
};
